// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getPickupLocations, selectQuery } from '../../feature';
import { changeQuery } from '../../feature/slice';

// Components
import { Input, Select } from '@/ui';
import { StyledFilterRow } from './PickupFiltersContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetPickupsDTO } from '../../dtos';
import { PickupFiltersFields } from '../../helpers/types';
import { getSettlementHeaderOptions } from '../../helpers/getOptions';
import { useAutocompleteRegions, useAutocompleteSettlements } from '../../hooks';

type IPartnersFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PickupFiltersContainer = ({ children, className }: IPartnersFiltersContainerProps) => {
  const { t } = useTranslation(['regions']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage pickup points.
  const canManagePickup = canManage(Resource.PICKUP);
  const { isLoading, onSearch, regionsOptions } = useAutocompleteRegions();
  const {
    isLoading: settlementsLoading,
    settlementsOptions,
    onSearch: onSearchSettlement,
  } = useAutocompleteSettlements();

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetPickupsDTO(searchParams);
        dispatch(getPickupLocations(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string | boolean) => {
    const newQuery = {
      ...query,
      [name]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSearchSettlement = (value: string) => onSearchSettlement(value, query?.[PickupFiltersFields.REGION]);

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Select
            filterOption={false}
            options={regionsOptions ?? []}
            placeholder={t('pickup_select')}
            onSearch={onSearch}
            onChange={handleSelectFilter(PickupFiltersFields.REGION)}
            showSearch
            loading={isLoading}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            filterOption={false}
            options={settlementsOptions ?? []}
            loading={settlementsLoading}
            onSearch={handleSearchSettlement}
            onChange={handleSelectFilter(PickupFiltersFields.SETTLEMENT)}
            placeholder={t('pickup_show_all')}
            size="large"
            showSearch
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('pickup_search')}
            name={PickupFiltersFields.ADDRESS}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th />
        <th>
          <Input
            placeholder={t('pickup_search')}
            name={PickupFiltersFields.PHONE}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getSettlementHeaderOptions(t)}
            placeholder={t('pickup_show_all')}
            onChange={handleBooleanFilter(PickupFiltersFields.IS_DEFAULT)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getSettlementHeaderOptions(t)}
            placeholder={t('pickup_show_all')}
            onChange={handleBooleanFilter(PickupFiltersFields.IS_ACTIVE)}
            size="large"
            allowClear
          />
        </th>
        {canManagePickup && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PickupFiltersContainer);
